import React, { useState, useCallback } from "react";
import {
    Box,
    Button,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";

import { Colors } from "../../../Styles/Colors";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import Loader from "../../../Components/Loader";

const UOM_OPTIONS = ["g", "kg", "ml", "L", "nos", "mg"];
const DURATION_UNITS = ["Hrs", "Mins", "Days"];
const GST_OPTIONS = [0, 5, 12, 18];

const defaultIngredient = () => ({
    stockItem: null,
    quantity: "",
    uom: "g",
});

export default function MedicineCreation() {
    const [ingredients, setIngredients] = useState([defaultIngredient()]);
    const [outputMedicine, setOutputMedicine] = useState(null);
    const [outputMedicineName, setOutputMedicineName] = useState("");
    const [duration, setDuration] = useState("12");
    const [durationUnit, setDurationUnit] = useState("Hrs");
    const [productQuantity, setProductQuantity] = useState("12");
    const [productUom, setProductUom] = useState("nos");
    const [batchNo, setBatchNo] = useState("AYD-00012");
    const [expiryDate, setExpiryDate] = useState(dayjs().add(1, "year"));
    const [pricePerGm, setPricePerGm] = useState("");
    const [pricePerQty, setPricePerQty] = useState("");
    const [gstPercentage, setGstPercentage] = useState(12);

    const [drugSearchOptions, setDrugSearchOptions] = useState([]);
    const [drugSearchLoading, setDrugSearchLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(false);
    const [errorMsgText, setErrorMsgText] = useState("");
    const [successMsg, setSuccessMsg] = useState(false);
    const [successMsgText, setSuccessMsgText] = useState("");

    const fetchDrugSearch = useCallback((query) => {
        if (!query || query.length < 2) {
            setDrugSearchOptions([]);
            return;
        }
        setDrugSearchLoading(true);
        const url = `${Serviceurls.AYURVEDA_CREATION_DRUG_SEARCH}?drug_name=${encodeURIComponent(query)}&ingredients=true`;
        RestAPIService.getAll(url)
            .then((res) => {
                if (res?.data?.status === "success" && Array.isArray(res.data.data)) {
                    setDrugSearchOptions(res.data.data);
                } else {
                    setDrugSearchOptions([]);
                }
            })
            .catch(() => setDrugSearchOptions([]))
            .finally(() => setDrugSearchLoading(false));
    }, []);

    const addIngredient = () => {
        setIngredients((prev) => [...prev, defaultIngredient()]);
    };

    const updateIngredient = (index, field, value) => {
        setIngredients((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const removeIngredient = (index) => {
        if (ingredients.length <= 1) return;
        setIngredients((prev) => prev.filter((_, i) => i !== index));
    };

    const clearForm = () => {
        setIngredients([defaultIngredient()]);
        setOutputMedicine(null);
        setOutputMedicineName("");
        setDuration("12");
        setDurationUnit("Hrs");
        setProductQuantity("12");
        setProductUom("nos");
        setBatchNo("AYD-00012");
        setExpiryDate(dayjs().add(1, "year"));
        setPricePerGm("");
        setPricePerQty("");
        setGstPercentage(12);
        setDrugSearchOptions([]);
    };

    const validateAndSubmit = () => {
        const hasValidIngredient = ingredients.some(
            (i) => i.stockItem && i.quantity && Number(i.quantity) > 0
        );
        if (!hasValidIngredient) {
            setErrorMsgText("Add at least one ingredient with drug, quantity and UOM.");
            setErrorMsg(true);
            return;
        }
        if (!outputMedicine && !outputMedicineName) {
            setErrorMsgText("Select or enter output Medicine Name.");
            setErrorMsg(true);
            return;
        }
        const drugMsId = outputMedicine?.drug_ms_id ?? null;
        if (!drugMsId && outputMedicineName) {
            setErrorMsgText("Select output medicine from search to get drug master id.");
            setErrorMsg(true);
            return;
        }
        if (!batchNo?.trim()) {
            setErrorMsgText("Enter Batch No.");
            setErrorMsg(true);
            return;
        }
        if (!expiryDate || !dayjs(expiryDate).isValid()) {
            setErrorMsgText("Select Expiry Date.");
            setErrorMsg(true);
            return;
        }
        const qty = Number(productQuantity);
        if (!productQuantity || isNaN(qty) || qty <= 0) {
            setErrorMsgText("Enter valid product quantity.");
            setErrorMsg(true);
            return;
        }
        const priceGm = Number(pricePerGm);
        const priceQty = Number(pricePerQty);
        if (isNaN(priceGm) || priceGm < 0 || isNaN(priceQty) || priceQty < 0) {
            setErrorMsgText("Enter valid price per gm and price per qty.");
            setErrorMsg(true);
            return;
        }

        const payload = {
            drug_ms_id: drugMsId,
            quantity: qty,
            uom: productUom,
            batch_no: batchNo.trim(),
            expiry_date: dayjs(expiryDate).format("YYYY-MM-DD"),
            price_per_gm: priceGm,
            price_per_qty: priceQty,
            gst_percentage: gstPercentage,
            ingredients: ingredients
                .filter((i) => i.stockItem && i.quantity && Number(i.quantity) > 0)
                .map((i) => ({
                    drug_ms_id: i.stockItem.drug_ms_id,
                    stock_id: i.stockItem.id,
                    quantity: Number(i.quantity),
                    uom: i.uom,
                })),
        };

        setSubmitLoading(true);
        setErrorMsg(false);
        setSuccessMsg(false);
        RestAPIService.create(payload, Serviceurls.AYURVEDA_MEDICINE_CREATION)
            .then((res) => {
                if (res?.data?.status === "success") {
                    setSuccessMsgText(res?.data?.message || "Added to stock successfully.");
                    setSuccessMsg(true);
                    clearForm();
                } else {
                    setErrorMsgText(res?.data?.message || "Failed to add to stock.");
                    setErrorMsg(true);
                }
            })
            .catch((e) => {
                setErrorMsgText(
                    e?.response?.data?.message || e?.message || "Failed to add to stock."
                );
                setErrorMsg(true);
            })
            .finally(() => setSubmitLoading(false));
    };

    const msgClose = () => {
        setErrorMsg(false);
        setErrorMsgText("");
        setSuccessMsg(false);
        setSuccessMsgText("");
    };

    const renderSectionLabel = (label) => (
        <Typography
            variant="subtitle2"
            sx={{
                fontWeight: 600,
                color: "#333",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 1,
            }}
        >
            {label}
        </Typography>
    );

    return (
        <Box
            sx={{
                minHeight: "85vh",
                width: "100%",
                backgroundColor: Colors.Background || "#f5f5f5",
                p: 2,
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                Medication Preparation
            </Typography>

            <Box
                sx={{
                    backgroundColor: "white",
                    borderRadius: 1,
                    boxShadow: 1,
                    p: 2,
                    maxWidth: 900,
                }}
            >
                {/* PREPARATION */}
                {renderSectionLabel("Preparation")}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                    {ingredients.map((row, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <Autocomplete
                                size="small"
                                sx={{ minWidth: 260 }}
                                options={drugSearchOptions}
                                getOptionLabel={(opt) =>
                                    typeof opt === "string"
                                        ? opt
                                        : `${opt.drug_name || ""} (${opt.batch_no || ""})`
                                }
                                value={row.stockItem}
                                onChange={(_, val) => updateIngredient(index, "stockItem", val)}
                                onInputChange={(_, value) => fetchDrugSearch(value)}
                                loading={drugSearchLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={`Select Medicine ${String(index + 1).padStart(2, "0")}`}
                                        variant="outlined"
                                    />
                                )}
                            />
                            <TextField
                                size="small"
                                type="number"
                                placeholder="Qty"
                                value={row.quantity}
                                onChange={(e) =>
                                    updateIngredient(index, "quantity", e.target.value)
                                }
                                sx={{ width: 100 }}
                                inputProps={{ min: 0 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                    value={row.uom}
                                    onChange={(e) =>
                                        updateIngredient(index, "uom", e.target.value)
                                    }
                                    displayEmpty
                                >
                                    {UOM_OPTIONS.map((u) => (
                                        <MenuItem key={u} value={u}>
                                            {u.toUpperCase()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {ingredients.length > 1 && (
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => removeIngredient(index)}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addIngredient}
                        size="small"
                        sx={{ alignSelf: "flex-start", textTransform: "none" }}
                    >
                        Add New Medicine
                    </Button>
                </Box>

                {/* PROCESS */}
                {renderSectionLabel("Process")}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            DURATION
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            sx={{ width: 80 }}
                            inputProps={{ min: 0 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value={durationUnit}
                                onChange={(e) => setDurationUnit(e.target.value)}
                            >
                                {DURATION_UNITS.map((u) => (
                                    <MenuItem key={u} value={u}>
                                        {u}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            QUANTITY
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            value={productQuantity}
                            onChange={(e) => setProductQuantity(e.target.value)}
                            sx={{ width: 80 }}
                            inputProps={{ min: 0 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                            <Select
                                value={productUom}
                                onChange={(e) => setProductUom(e.target.value)}
                            >
                                {UOM_OPTIONS.map((u) => (
                                    <MenuItem key={u} value={u}>
                                        {u.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            BATCH NO
                        </Typography>
                        <TextField
                            size="small"
                            value={batchNo}
                            onChange={(e) => setBatchNo(e.target.value)}
                            placeholder="Batch No"
                            sx={{ width: 140 }}
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            EXPIRY DATE
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={expiryDate}
                                onChange={(d) => setExpiryDate(d)}
                                inputFormat="MM-YYYY"
                                views={["year", "month"]}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" sx={{ width: 140 }} />
                                )}
                            />
                        </LocalizationProvider>
                    </Box>
                </Box>

                {/* OUTPUT */}
                {renderSectionLabel("Output")}
                <Box sx={{ mb: 2 }}>
                    <Autocomplete
                        size="small"
                        sx={{ maxWidth: 400 }}
                        options={drugSearchOptions}
                        getOptionLabel={(opt) =>
                            typeof opt === "string" ? opt : opt.drug_name || ""
                        }
                        value={outputMedicine}
                        onChange={(_, val) => {
                            setOutputMedicine(val);
                            setOutputMedicineName(val?.drug_name ?? "");
                        }}
                        onInputChange={(_, value) => {
                            setOutputMedicineName(value);
                            fetchDrugSearch(value);
                        }}
                        loading={drugSearchLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Medicine Name"
                                placeholder="Medicine Name"
                                variant="outlined"
                            />
                        )}
                    />
                </Box>

                {/* PRICE CALCULATION */}
                {renderSectionLabel("Price Calculation")}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                    <TextField
                        size="small"
                        label="Price/gm"
                        type="number"
                        value={pricePerGm}
                        onChange={(e) => setPricePerGm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Typography variant="body2">₹</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 140 }}
                        inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                        size="small"
                        label="Price/Qty"
                        type="number"
                        value={pricePerQty}
                        onChange={(e) => setPricePerQty(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Typography variant="body2">₹</Typography>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 140 }}
                        inputProps={{ min: 0, step: 0.01 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>GST(%)</InputLabel>
                        <Select
                            value={gstPercentage}
                            onChange={(e) => setGstPercentage(Number(e.target.value))}
                            label="GST(%)"
                        >
                            {GST_OPTIONS.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {g}%
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={clearForm}
                        sx={{ borderColor: "#999", color: "#666" }}
                    >
                        Clear
                    </Button>
                    <Button
                        variant="contained"
                        onClick={validateAndSubmit}
                        disabled={submitLoading}
                        sx={{
                            backgroundColor: "#04B7B1",
                            "&:hover": { backgroundColor: "#039a95" },
                        }}
                    >
                        {submitLoading ? "Adding..." : "Add to Stock"}
                    </Button>
                </Box>
            </Box>

            {errorMsg && (
                <ToastMsg severity="error" msg={errorMsgText} msgPop={msgClose} />
            )}
            {successMsg && (
                <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />
            )}
            {submitLoading && <Loader />}
        </Box>
    );
}
